/**
 * App.tsx
 * ────────
 * Single-entry-point RBAC routing tree.
 *
 * All users land at / (Login).  After JWT auth the AuthContext redirects
 * to the role-specific dashboard:
 *
 *   pilot    → /dashboard/missions     (PilotDashboard — mission selection)
 *   pilot    → /dashboard/flight      (PilotInterface — upload flow)
 *   pilot    → /dashboard/retake      (RetakeScreen — exception queue)
 *   manager  → /dashboard/operations   (ManagerConsole)
 *   admin    → /dashboard/operations   (ManagerConsole, full access)
 *   customer → /dashboard/inventory    (CustomerDashboard)
 *
 * ProtectedRoute enforces roles at the component level so direct URL
 * navigation to a restricted path produces an /unauthorized redirect.
 */

import { Routes, Route, Navigate } from 'react-router-dom';

import { ProtectedRoute } from '@/components/ProtectedRoute';
import Login               from '@/views/Login';
import Unauthorized        from '@/views/Unauthorized';
import ForgotPassword      from '@/views/ForgotPassword';
import ResetPassword       from '@/views/ResetPassword';
import PilotDashboard     from '@/views/PilotDashboard';
import PilotInterface      from '@/views/PilotInterface';
import ExceptionTriage     from '@/views/ExceptionTriage';
import RetakeScreen        from '@/views/RetakeScreen';
import ManagerConsole      from '@/views/ManagerConsole';
import CustomerDashboard   from '@/views/CustomerDashboard';
import InventoryReport     from '@/views/InventoryReport';

/** Roles permitted to view the customer report experience. */
const REPORT_ROLES = ['customer', 'manager', 'admin', 'super_admin'] as const;

/**
 * Standalone report deployment (report.infiniumrobotics.com).
 * Exposes ONLY the login + report experience — no pilot / scan / lab routes.
 * The customer inventory dashboard is the authenticated landing; it links
 * into the per-mission report view.  Post-login role redirects that target
 * pilot/manager dashboards are bounced to the inventory landing so no role
 * dead-ends on this host.
 */
function ReportApp() {
  return (
    <Routes>
      {/* ── Public / auth ─────────────────────────────────────────────────── */}
      <Route path="/"                  element={<Login />} />
      <Route path="/unauthorized"      element={<Unauthorized />} />
      <Route path="/forgot-password"   element={<ForgotPassword />} />
      <Route path="/reset-password"    element={<ResetPassword />} />

      {/* ── Authenticated landing (report list / inventory browser) ───────── */}
      <Route
        path="/dashboard/inventory"
        element={
          <ProtectedRoute allowedRoles={[...REPORT_ROLES]}>
            <CustomerDashboard />
          </ProtectedRoute>
        }
      />

      {/* ── Per-mission customer report ───────────────────────────────────── */}
      <Route
        path="/dashboard/report/:missionId"
        element={
          <ProtectedRoute allowedRoles={[...REPORT_ROLES]}>
            <InventoryReport />
          </ProtectedRoute>
        }
      />

      {/* Manager/admin login redirect target → inventory landing on this host. */}
      <Route path="/dashboard/operations" element={<Navigate to="/dashboard/inventory" replace />} />

      {/* Any other path → login. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  if (typeof window !== 'undefined' && window.location.hostname === 'report.infiniumrobotics.com') {
    return <ReportApp />;
  }

  return (
    <Routes>
      {/* ── Public ────────────────────────────────────────────────────────── */}
      <Route path="/"                  element={<Login />} />
      <Route path="/unauthorized"      element={<Unauthorized />} />
      <Route path="/forgot-password"   element={<ForgotPassword />} />
      <Route path="/reset-password"    element={<ResetPassword />} />

      {/* ── Pilot mission dashboard ───────────────────────────────────────── */}
      <Route
        path="/dashboard/missions"
        element={
          <ProtectedRoute allowedRoles={['pilot', 'manager', 'admin', 'super_admin']}>
            <PilotDashboard />
          </ProtectedRoute>
        }
      />

      {/* ── Pilot upload interface ────────────────────────────────────────── */}
      <Route
        path="/dashboard/flight"
        element={
          <ProtectedRoute allowedRoles={['pilot', 'manager', 'admin', 'super_admin']}>
            <PilotInterface />
          </ProtectedRoute>
        }
      />

      {/* ── Exception triage (Step 7) — pilot + managers ─────────────────── */}
      <Route
        path="/dashboard/triage/:missionId"
        element={
          <ProtectedRoute allowedRoles={['pilot', 'manager', 'admin', 'super_admin']}>
            <ExceptionTriage />
          </ProtectedRoute>
        }
      />

      {/* ── Pilot retake queue ────────────────────────────────────────────── */}
      <Route
        path="/dashboard/retake"
        element={
          <ProtectedRoute allowedRoles={['pilot', 'manager', 'admin', 'super_admin']}>
            <RetakeScreen />
          </ProtectedRoute>
        }
      />

      {/* ── Operations manager / admin ────────────────────────────────────── */}
      <Route
        path="/dashboard/operations"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'super_admin']}>
            <ManagerConsole />
          </ProtectedRoute>
        }
      />

      {/* ── Mission inventory report (Step 9) — customers / managers / admins ─ */}
      <Route
        path="/dashboard/report/:missionId"
        element={
          <ProtectedRoute allowedRoles={['customer', 'manager', 'admin', 'super_admin']}>
            <InventoryReport />
          </ProtectedRoute>
        }
      />

      {/* ── Customer inventory ────────────────────────────────────────────── */}
      <Route
        path="/dashboard/inventory"
        element={
          <ProtectedRoute allowedRoles={['pilot', 'customer', 'manager', 'admin', 'super_admin']}>
            <CustomerDashboard />
          </ProtectedRoute>
        }
      />

      {/* ── Catch-all: redirect unknown paths to login ────────────────────── */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
